import { Key, ReactNode, useState } from "react";
import { Table } from "@mantine/core";
import { PaginatedTable } from "./PaginatedTable";

export interface Column<T> {
  key: string;
  label: string;
  numeric?: boolean;
  // How to extract the sortable value (null/undefined sort last).
  get: (row: T) => number | string | null | undefined;
  // How to render the cell.
  render: (row: T) => ReactNode;
}

interface SortableTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: keyof T;
  initialSortKey?: string;
  initialSortDir?: 1 | -1;
  pageSize?: number;
  emptyMessage?: string;
}

export function SortableTable<T>({
  columns,
  rows,
  rowKey,
  initialSortKey,
  initialSortDir = -1,
  pageSize = 20,
  emptyMessage,
}: SortableTableProps<T>) {
  const [sortKey, setSortKey] = useState<string>(
    initialSortKey ?? columns[0]?.key ?? "",
  );
  const [sortDir, setSortDir] = useState<1 | -1>(initialSortDir);

  const col = columns.find((c) => c.key === sortKey) ?? columns[0];
  const sorted = rows.slice().sort((a, b) => {
    const va = col.get(a);
    const vb = col.get(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (va < vb) return -1 * sortDir;
    if (va > vb) return 1 * sortDir;
    return 0;
  });

  const head = (
    <Table.Tr>
      {columns.map((c) => {
        const isActive = c.key === sortKey;
        let arrow = "";
        if (isActive) arrow = sortDir < 0 ? " ↓" : " ↑";
        return (
          <Table.Th
            key={c.key}
            ta={c.numeric ? "right" : "left"}
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => {
              if (sortKey === c.key) {
                setSortDir((d) => (d === 1 ? -1 : 1));
              } else {
                setSortKey(c.key);
                setSortDir(c.numeric ? -1 : 1);
              }
            }}
          >
            {c.label}
            {arrow}
          </Table.Th>
        );
      })}
    </Table.Tr>
  );

  const renderedRows = sorted.map((row) => (
    <Table.Tr key={row[rowKey] as Key}>
      {columns.map((c) => (
        <Table.Td key={c.key} ta={c.numeric ? "right" : "left"}>
          {c.render(row)}
        </Table.Td>
      ))}
    </Table.Tr>
  ));

  return (
    <PaginatedTable
      head={head}
      rows={renderedRows}
      pageSize={pageSize}
      emptyMessage={emptyMessage}
    />
  );
}
